import type { IntegrationFormValue, IntegrationSaveOutcome } from './integrationFormSchema';
import { serializeIntegrationFormValue } from './integrationFormModel';
import type { Integration } from './model';
import type { PersistedTargeting } from '../../targeting/model';

export interface IntegrationTargetingCsvSaveDependencies {
  create: (
    demandPartnerId: string,
    payload: ReturnType<typeof serializeIntegrationFormValue>,
  ) => Promise<{ id?: string | undefined } | undefined>;
  get: (demandPartnerId: string, integrationId: string) => Promise<Integration>;
  importBundlesCsv: (targetingId: string, file: File) => Promise<number>;
  importIpsCsv: (targetingId: string, file: File) => Promise<number>;
  update: (
    demandPartnerId: string,
    integrationId: string,
    payload: ReturnType<typeof serializeIntegrationFormValue>,
  ) => Promise<unknown>;
}

export interface IntegrationTargetingCsvSaveResult {
  /** First CSV import error, so the caller can show the backend message. */
  csvError?: unknown;
  csvFailed: boolean;
  integrationId: string;
  outcome: IntegrationSaveOutcome;
}

export async function saveIntegrationWithTargetingCsv({
  dependencies,
  integrationId,
  onIntegrationIdResolved,
  value,
}: {
  dependencies: IntegrationTargetingCsvSaveDependencies;
  integrationId: string;
  onIntegrationIdResolved?: ((integrationId: string) => void) | undefined;
  value: IntegrationFormValue;
}): Promise<IntegrationTargetingCsvSaveResult> {
  const payload = serializeIntegrationFormValue(value, integrationId || undefined);
  let savedIntegrationId = integrationId;
  if (savedIntegrationId) {
    await dependencies.update(value.demandPartnerId, savedIntegrationId, payload);
  } else {
    const created = await dependencies.create(value.demandPartnerId, payload);
    savedIntegrationId = created?.id ?? '';
    if (!savedIntegrationId) throw new Error('Integration create response did not include id.');
  }
  onIntegrationIdResolved?.(savedIntegrationId);

  const outcome: IntegrationSaveOutcome = {};
  if (!value.pendingBundleCsv && !value.pendingIpCsv) {
    return { csvFailed: false, integrationId: savedIntegrationId, outcome };
  }

  let targetingId = value.targetingId;
  try {
    if (!targetingId) {
      const saved = await dependencies.get(value.demandPartnerId, savedIntegrationId);
      const targeting = saved.targeting as PersistedTargeting | null | undefined;
      targetingId = targeting?.id ?? '';
    }
  } catch (error) {
    return { csvError: error, csvFailed: true, integrationId: savedIntegrationId, outcome };
  }
  if (!targetingId) {
    return { csvFailed: true, integrationId: savedIntegrationId, outcome };
  }
  outcome.targetingId = targetingId;

  let csvError: unknown;
  if (value.pendingBundleCsv) {
    try {
      outcome.bundlesCount = await dependencies.importBundlesCsv(
        targetingId,
        value.pendingBundleCsv,
      );
      outcome.clearBundleCsv = true;
    } catch (error) {
      csvError = error;
    }
  }
  if (value.pendingIpCsv) {
    try {
      outcome.ipsCount = await dependencies.importIpsCsv(targetingId, value.pendingIpCsv);
      outcome.clearIpCsv = true;
    } catch (error) {
      csvError ??= error;
    }
  }
  if (csvError === undefined) {
    return { csvFailed: false, integrationId: savedIntegrationId, outcome };
  }
  return { csvError, csvFailed: true, integrationId: savedIntegrationId, outcome };
}
